'use client';

import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/template/Header';
import { URI } from '../../domain/constant';
import { useAllEmployees } from '../../shared/hooks/UseEmployee';
import './_components/modern-employee-styles.css';
import api from '../../domain/api';
import uploadPic from '../../assets/upload.jpeg';

const InteractiveEmployeeReports = () => {
  const [employees, setEmployees] = useState([]);
  const [employees_loading, setEmployeesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  // Fetch employees only once
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get(`/employees?limit=1000`);
        setEmployees(data.employees);
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchData();
  }, []);

  // Memoize CEO and employeesByDepartment to avoid unnecessary recalculations
  const ceo = useMemo(() => {
    return employees.find(
      (emp) =>
        emp.designation === 'Founder and CEO' &&
        emp.employee_status === 'Working'
    );
  }, [employees]);

  const employeesByDepartment = useMemo(() => {
    const otherEmployees = employees.filter(
      (emp) =>
        emp.designation !== 'Founder and CEO' &&
        emp.employee_status === 'Working'
    );
    const departmentGroups = {};
    otherEmployees.forEach((emp) => {
      const designation = emp.designation;
      let department = 'Other';
      if (
        designation.toLowerCase().includes('developer') ||
        designation.toLowerCase().includes('programmer') ||
        designation.toLowerCase().includes('engineer') ||
        designation.toLowerCase().includes('frontend') ||
        designation.toLowerCase().includes('backend') ||
        designation.toLowerCase().includes('fullstack') ||
        designation.toLowerCase().includes('software')
      ) {
        department = 'Development';
      } else if (
        designation.toLowerCase().includes('designer') ||
        designation.toLowerCase().includes('ui') ||
        designation.toLowerCase().includes('ux') ||
        designation.toLowerCase().includes('graphic')
      ) {
        department = 'Design';
      } else if (
        designation.toLowerCase().includes('manager') ||
        designation.toLowerCase().includes('lead') ||
        designation.toLowerCase().includes('head') ||
        designation.toLowerCase().includes('director')
      ) {
        department = 'Management';
      } else if (
        designation.toLowerCase().includes('marketing') ||
        designation.toLowerCase().includes('sales') ||
        designation.toLowerCase().includes('business')
      ) {
        department = 'Marketing & Sales';
      } else if (
        designation.toLowerCase().includes('hr') ||
        designation.toLowerCase().includes('human') ||
        designation.toLowerCase().includes('admin') ||
        designation.toLowerCase().includes('operations')
      ) {
        department = 'Operations';
      } else if (
        designation.toLowerCase().includes('qa') ||
        designation.toLowerCase().includes('test') ||
        designation.toLowerCase().includes('quality')
      ) {
        department = 'Quality Assurance';
      }
      if (!departmentGroups[department]) {
        departmentGroups[department] = [];
      }
      departmentGroups[department].push(emp);
    });
    return departmentGroups;
  }, [employees]);

  const getProfileImage = (employee) => {
    if (employee.image && employee.image.trim() !== '') {
      return `${URI}${employee.image}`;
    } else {
      return uploadPic;
    }
  };

  // Filter employees by department and search term
  const getFilteredEmployeesByDepartment = () => {
    const filtered = {};
    Object.keys(employeesByDepartment).forEach((dept) => {
      const deptEmployees = employeesByDepartment[dept].filter((emp) => {
        const matchesSearch =
          emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.designation.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDepartment =
          selectedDepartment === 'all' || dept === selectedDepartment;
        return matchesSearch && matchesDepartment;
      });
      if (deptEmployees.length > 0) {
        filtered[dept] = deptEmployees;
      }
    });
    return filtered;
  };

  const filteredDepartments = getFilteredEmployeesByDepartment();
  const allDepartments = Object.keys(employeesByDepartment);
  const totalEmployees = Object.values(employeesByDepartment).flat().length;

  if (employees_loading) {
    return (
      <div className='loading-container'>
        <Header />
        <div className='loading-content'>
          <div className='loading-spinner'></div>
          <h3>Loading Our Amazing Team...</h3>
          <p>Please wait while we gather our team information</p>
        </div>
      </div>
    );
  }

  return (
    <div className='pace-done '>
      <div>
        <Header />

        {/* Hero Section */}
        <section className='hero-section'>
          <div className='hero-background'>
            <div className='floating-shapes'>
              <div className='shape shape-1'></div>
              <div className='shape shape-2'></div>
              <div className='shape shape-3'></div>
            </div>
            <div className='container-fluid'>
              <div className='hero-content'>
                <div className='hero-badge'>Meet Our Team</div>
                <h1 className='hero-title'>
                  Exceptional People,
                  <br />
                  <span className='gradient-text'>Extraordinary Results</span>
                </h1>
                <p className='hero-description'>
                  Discover the talented individuals who drive innovation and
                  success at Luhaif Digitech
                </p>
                <div className='hero-stats'>
                  <div className='stat-card'>
                    <div className='stat-number'>
                      {totalEmployees + (ceo ? 1 : 0)}
                    </div>
                    <div className='stat-label'>Team Members</div>
                  </div>
                  <div className='stat-card'>
                    <div className='stat-number'>{allDepartments.length}</div>
                    <div className='stat-label'>Departments</div>
                  </div>
                  <div className='stat-card'>
                    <div className='stat-number'>8+</div>
                    <div className='stat-label'>Years Experience</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CEO Section */}
        {ceo && (
          <section className='ceo-spotlight'>
            <div className='container-fluid'>
              <div className='ceo-container'>
                <div className='row align-items-center'>
                  <div className='col-lg-5'>
                    <div className='ceo-profile'>
                      <div className='profile-image-container'>
                        <div className='image-glow'></div>
                        <img
                          src={getProfileImage(ceo) || '/placeholder.svg'}
                          alt={`${ceo.name} - CEO`}
                          className='ceo-image'
                        />
                        <div className='ceo-badge'>
                          <span>👑</span>
                          <span>CEO</span>
                        </div>
                      </div>
                      <div className='ceo-info'>
                        <h2 className='ceo-name'>{ceo.name}</h2>
                        <p className='ceo-title'>{ceo.designation}</p>
                        <Link
                          to={`/employees/${ceo._id}/view`}
                          className='view-profile-btn'
                        >
                          <span>View Full Profile</span>
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                          >
                            <path
                              d='M7 17L17 7M17 7H7M17 7V17'
                              stroke='currentColor'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-7'>
                    <div className='ceo-content'>
                      <div className='content-header'>
                        <h3>Leadership Excellence</h3>
                        <div className='header-line'></div>
                      </div>
                      <div className='leadership-highlights'>
                        <div className='highlight-item'>
                          <div className='highlight-icon'>🎯</div>
                          <div className='highlight-content'>
                            <h4>Visionary Leadership</h4>
                            <p>
                              8+ years of industry expertise driving digital
                              transformation and business growth
                            </p>
                          </div>
                        </div>
                        <div className='highlight-item'>
                          <div className='highlight-icon'>🏆</div>
                          <div className='highlight-content'>
                            <h4>Award-Winning Entrepreneur</h4>
                            <p>
                              IIM Nagpur alumnus with recognition in
                              philanthropy and youth development
                            </p>
                          </div>
                        </div>
                        <div className='highlight-item'>
                          <div className='highlight-icon'>🚀</div>
                          <div className='highlight-content'>
                            <h4>Innovation Driver</h4>
                            <p>
                              Strategic project management across international
                              markets and organizations
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Team Section */}
        <section className='team-section  '>
          <div className='container-fluid'>
            <div className='section-header'>
              <h2 className='section-title'>Our Team</h2>
              <p className='section-subtitle'>
                Meet our talented professionals organized by expertise
              </p>
            </div>

            {/* Search and Filter */}
            <div className='team-controls'>
              <div className='search-container'>
                <div className='search-input-wrapper'>
                  <svg
                    className='search-icon'
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <circle
                      cx='11'
                      cy='11'
                      r='8'
                      stroke='currentColor'
                      strokeWidth='2'
                    />
                    <path
                      d='m21 21-4.35-4.35'
                      stroke='currentColor'
                      strokeWidth='2'
                    />
                  </svg>
                  <input
                    type='text'
                    placeholder='Search team members...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className='search-input'
                  />
                </div>
              </div>
              <div className='filter-container'>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className='department-filter'
                >
                  <option value='all'>All Departments</option>
                  {allDepartments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept} ({employeesByDepartment[dept].length})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Department Sections */}
            {Object.keys(filteredDepartments).length > 0 ? (
              Object.keys(filteredDepartments).map((department, deptIndex) => (
                <div
                  key={department}
                  className='department-section'
                  style={{ animationDelay: `${deptIndex * 0.2}s` }}
                >
                  <div className='department-header'>
                    <div className='department-title-wrapper'>
                      <h3 className='department-title'>{department}</h3>
                      <div className='department-count'>
                        {filteredDepartments[department].length} Members
                      </div>
                    </div>
                    <div className='department-line'></div>
                  </div>

                  <div className='department-grid'>
                    {filteredDepartments[department].map((employee, index) => (
                      <div
                        key={employee._id}
                        className='team-card'
                        style={{
                          animationDelay: `${deptIndex * 0.2 + index * 0.1}s`,
                        }}
                      >
                        <Link
                          to={`/employees/${employee._id}/view`}
                          className='card-link'
                        >
                          <div className='card-inner'>
                            <div className='card-front'>
                              <div className='employee-image-container'>
                                <img
                                  src={
                                    getProfileImage(employee) ||
                                    '/placeholder.svg' ||
                                    '/placeholder.svg'
                                  }
                                  alt={employee.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                  // className='employee-image'
                                />
                                <div className='image-overlay'>
                                  <div className='overlay-content'>
                                    <span>View Profile</span>
                                  </div>
                                </div>
                              </div>
                              <div className='employee-info'>
                                <h3 className='employee-name'>
                                  {employee.name}
                                </h3>
                                <p className='employee-designation'>
                                  {employee.designation}
                                </p>
                                <div className='employee-status'>
                                  <div className='status-dot'></div>
                                  <span>Active</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className='no-results'>
                <div className='no-results-icon'>🔍</div>
                <h3>No team members found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className='cta-section'>
          <div className='container-fluid'>
            <div className='cta-container'>
              <div className='row align-items-center'>
                <div className='col-lg-6'>
                  <div className='cta-content'>
                    <h2 className='cta-title'>Ready to Work With Us?</h2>
                    <p className='cta-description'>
                      Discover how our exceptional team can help transform your
                      business with innovative digital solutions.
                    </p>
                    <a
                      href='https://luhaifdigitech.com/'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='cta-button'
                    >
                      <span>Visit Luhaif Digitech</span>
                      <div className='button-arrow'>
                        <svg
                          width='16'
                          height='16'
                          viewBox='0 0 24 24'
                          fill='none'
                        >
                          <path
                            d='M7 17L17 7M17 7H7M17 7V17'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </div>
                    </a>
                  </div>
                </div>
                <div className='col-lg-6'>
                  <div className='cta-visual'>
                    <img
                      src='/images/undraw_progressive_app_m-9-ms.svg'
                      alt='Digital Solutions'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default InteractiveEmployeeReports;
